/**
 * Walk-In Queue Display — kiosk-friendly, large format
 * Real-time via WebSocket. Touch-optimized.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import QRCode from "qrcode";
import { Clock, Users, CheckCircle, AlertTriangle, Loader2, ArrowRightCircle, QrCode } from "lucide-react";
import { useDisplayData, useDisplayMutation } from "@/hooks/use-display";
import { formatTime } from "@/lib/i18n";
import type {
  WalkInQueueCallNextResult,
  WalkInQueueData,
  WalkInQueueQrData,
  DisplayQueueEntry,
} from "@ease/shared";

const DISPLAY_SERVER_URL = import.meta.env.VITE_DISPLAY_SERVER_URL ?? "";

export default function WalkInQueueDisplay() {
  const [searchParams] = useSearchParams();
  const displayId = searchParams.get("displayId") ?? "";
  const token = searchParams.get("token") ?? "";
  const operatorMode = searchParams.get("operator") === "1";

  const { data, isLoading, error, isStale, lastUpdatedAt } = useDisplayData<WalkInQueueData>(displayId, token, "walk-in-queue");
  const callNextMutation = useDisplayMutation<never, WalkInQueueCallNextResult>(
    displayId,
    token,
    "walk-in-queue/call-next",
  );
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [callNextMessage, setCallNextMessage] = useState<string | null>(null);

  const joinUrl = useMemo(() => {
    if (!displayId || !token) {
      return null;
    }

    const baseUrl = typeof window !== "undefined"
      ? window.location.origin
      : "";
    return `${baseUrl}/display/kiosk?displayId=${encodeURIComponent(displayId)}&mode=walk-in`;
  }, [displayId, token]);

  useEffect(() => {
    let isCancelled = false;

    async function loadQrCode() {
      if (!displayId || !token || !DISPLAY_SERVER_URL || !joinUrl) {
        return;
      }

      try {
        const response = await fetch(`${DISPLAY_SERVER_URL}/display/${displayId}/walk-in-queue/qr`, {
          headers: { "X-Display-Token": token },
          signal: AbortSignal.timeout(10_000),
        });

        if (!response.ok) {
          throw new Error("Unable to load queue QR");
        }

        const json = await response.json() as { data: WalkInQueueQrData };
        const qrJoinUrl = `${joinUrl}&joinToken=${encodeURIComponent(json.data.joinToken)}${json.data.serviceId ? `&serviceId=${encodeURIComponent(json.data.serviceId)}` : ""}`;
        const dataUrl = await QRCode.toDataURL(qrJoinUrl, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 256,
        });

        if (!isCancelled) {
          setQrCodeUrl(dataUrl);
          setQrError(null);
        }
      } catch (caughtError) {
        if (!isCancelled) {
          setQrCodeUrl(null);
          setQrError(caughtError instanceof Error ? caughtError.message : "Unable to load queue QR.");
        }
      }
    }

    void loadQrCode();
    return () => {
      isCancelled = true;
    };
  }, [displayId, token, joinUrl]);

  const handleCallNext = async () => {
    const result = await callNextMutation.mutate();
    if (!result) {
      return;
    }

    if (result.calledEntry) {
      setCallNextMessage(`Now calling ticket #${result.calledEntry.ticketNumber} (${result.calledEntry.customerName})`);
      return;
    }

    setCallNextMessage("No one is waiting in the queue.");
  };

  if (!displayId || !token) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Display not configured. Provide displayId and token.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[var(--color-primary)]" aria-label="Loading display data" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-12 w-12 mx-auto text-[var(--color-destructive)]" aria-label="Error" />
          <p className="text-2xl text-[var(--color-destructive)]">Unable to load queue data</p>
          <p className="text-lg text-[var(--color-muted-foreground)]">{error}</p>
        </div>
      </div>
    );
  }

  const entries: readonly DisplayQueueEntry[] = data?.entries ?? [];

  return (
    <div className="min-h-screen bg-[var(--color-background)] p-8 flex flex-col">
      {isStale && (
        <div className="mb-4 rounded-xl bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 px-6 py-3 text-center">
          <p className="text-lg text-[var(--color-warning)]">Data may be outdated. Reconnecting...</p>
        </div>
      )}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-[var(--color-foreground)]">Walk-In Queue</h1>
        {data?.currentlyServing && (
          <div className="mt-3 flex items-center justify-center gap-2 text-xl text-[var(--color-success)]">
            <CheckCircle className="h-6 w-6" aria-hidden="true" />
            Now serving: {data.currentlyServing}
          </div>
        )}
        {data?.stats && (
          <div className="mt-2 text-lg text-[var(--color-muted-foreground)]">
            {data.stats.totalWaiting} waiting &middot; ~{data.stats.avgWaitMinutes} min avg
          </div>
        )}
        {operatorMode && (
          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => { void handleCallNext(); }}
              disabled={callNextMutation.isLoading}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {callNextMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRightCircle className="h-4 w-4" aria-hidden="true" />
              )}
              Call next customer
            </button>
            {(callNextMessage || callNextMutation.error) && (
              <p className="text-sm text-[var(--color-muted-foreground)]">
                {callNextMutation.error ?? callNextMessage}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 space-y-4 max-w-2xl mx-auto w-full">
        {entries.map((entry: DisplayQueueEntry) => (
          <div key={entry.position} className={`rounded-2xl border-2 p-6 transition-all ${entry.status === "next" ? "border-[var(--color-success)] bg-[var(--color-success)]/5 scale-105" : "border-[var(--color-border)] bg-[var(--color-surface)]"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-5">
                <div className={`text-4xl font-bold ${entry.status === "next" ? "text-[var(--color-success)]" : "text-[var(--color-muted-foreground)]"}`}>#{entry.position}</div>
                <div>
                  <div className="text-2xl font-semibold text-[var(--color-foreground)]">{entry.name}</div>
                  <div className="text-lg text-[var(--color-muted-foreground)]">{entry.service}</div>
                </div>
              </div>
              <div className="text-right">
                {entry.status === "next" ? (
                  <div className="text-2xl font-bold text-[var(--color-success)]">You&apos;re next!</div>
                ) : (
                  <div className="flex items-center gap-2 text-xl text-[var(--color-muted-foreground)]">
                    <Clock className="h-5 w-5" aria-hidden="true" />
                    ~{entry.estimatedWait} min
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {entries.length === 0 && (
          <div className="text-center py-20">
            <Users className="h-16 w-16 mx-auto text-[var(--color-muted-foreground)] mb-4" aria-hidden="true" />
            <p className="text-2xl text-[var(--color-muted-foreground)]">No one in queue</p>
          </div>
        )}
      </div>
      <div className="mx-auto mt-8 w-full max-w-2xl rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)]/10 px-3 py-1 text-xs font-semibold text-[var(--color-primary)]">
            <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
            Scan to join in under 10 seconds
          </div>
          {qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt="QR code that opens the walk-in queue join page"
              className="h-48 w-48 rounded-2xl bg-white p-3"
            />
          ) : (
            <div className="flex h-48 w-48 items-center justify-center rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-background)] text-sm text-[var(--color-muted-foreground)]">
              {qrError ?? "Generating QR code..."}
            </div>
          )}
          <p className="max-w-md text-sm text-[var(--color-muted-foreground)]">
            Customers can scan on their phone, enter their name, and join the live queue without waiting at the desk.
          </p>
        </div>
      </div>
      <div className="text-center mt-8 text-sm text-[var(--color-muted-foreground)]">
        Last updated: {lastUpdatedAt ? formatTime(lastUpdatedAt) : "..."}
      </div>
    </div>
  );
}
